import { useCallback, useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { PRIORITIES, STATUSES, CATEGORIES } from "../../components/DeskLayout";
import { TicketTable } from "./DeskDashboard";

const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [filters, setFilters] = useState({ status: "", priority: "", q: "" });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "P3", category: "Other" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    api.get(`/helpdesk/tickets?${params}`).then((r) => setTickets(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/helpdesk/tickets", form);
      toast.success(`Ticket ${data.number} created`);
      setShow(false);
      setForm({ title: "", description: "", priority: "P3", category: "Other" });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="desk-tickets-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold font-heading">Tickets</h1>
        <button data-testid="new-ticket-button" onClick={() => setShow(true)}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
          <Plus size={15} /> New ticket
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="ticket-search" placeholder="Search title or number..." value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className={inputCls + " w-64"} />
        <select data-testid="ticket-status-filter" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className={inputCls}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select data-testid="ticket-priority-filter" value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} className={inputCls}>
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <TicketTable tickets={tickets} />

      {show && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="new-ticket-modal">
          <form onSubmit={create} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-lg w-full p-7">
            <h3 className="font-semibold font-heading text-lg mb-4">New ticket</h3>
            <label className="text-sm font-medium text-neutral-700">Title</label>
            <input data-testid="ticket-title-input" required minLength={3} value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Description</label>
            <textarea data-testid="ticket-desc-input" rows={4} value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><label className="text-sm font-medium text-neutral-700">Priority</label>
                <select data-testid="ticket-priority-input" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className={inputCls + " w-full mt-1"}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select></div>
              <div><label className="text-sm font-medium text-neutral-700">Category</label>
                <select data-testid="ticket-category-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inputCls + " w-full mt-1"}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
            </div>
            <div className="flex gap-2 mt-6">
              <button data-testid="ticket-create-button" disabled={busy}
                className="flex-1 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                {busy ? "Creating..." : "Create ticket"}
              </button>
              <button type="button" data-testid="ticket-cancel-button" onClick={() => setShow(false)}
                className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
